/**
 * GrowhatsWebphone Core — Wavoip-compatible API
 *
 * Connects to gapiv2 server (HTTP API + Socket.io + WebSocket audio).
 * Uses QR code channel routes for all call operations.
 * API surface mirrors @wavoip/wavoip-webphone for drop-in replacement.
 */

import { io, Socket } from 'socket.io-client';
import { EventEmitter } from './events';
import { AudioBridge } from './audio';

/**
 * Socket.io base URL. Default: same origin as `serverUrl` (gapiv2 proxies
 * `/socket.io` on PORT → SOCKET_PORT). Pass `socketUrl` to override.
 */
export function resolveSocketUrl(
  serverUrl: string,
  socketUrl?: string,
  viaApiProxy = true,
): string {
  const explicit = socketUrl?.trim().replace(/\/$/, '');
  if (explicit) return explicit;

  const base = serverUrl.replace(/\/$/, '');
  if (viaApiProxy) return base;

  return base.replace(/:4000$/, ':4001');
}
import type {
  WebphoneConfig,
  WebphoneEvents,
  WebphoneAPI,
  CallOffer,
  CallActive,
  CallOutgoing,
  CallStatus,
  CallDirection,
  CallStats,
  Device,
  DeviceStatus,
  Notification,
  NotificationType,
  PositionPreset,
  RecordingConfig,
  RecordingResult,
  AgentInfo,
  OnlineAgent,
  CallTakenEvent,
  AgentDuplicateSessionEvent,
} from './types';

const WEBPHONE_INSTANCE_STORAGE_KEY = 'growhats_webphone_client_instance';

/** Stable per-tab id so two tabs with the same CRM user do not fight over calls. */
function resolveClientInstanceId(agent?: AgentInfo | null): string {
  if (agent?.clientInstanceId?.trim()) return agent.clientInstanceId.trim().slice(0, 128);
  if (typeof sessionStorage !== 'undefined') {
    let id = sessionStorage.getItem(WEBPHONE_INSTANCE_STORAGE_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `inst-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem(WEBPHONE_INSTANCE_STORAGE_KEY, id);
    }
    return id;
  }
  return `ephemeral-${Date.now()}`;
}

/** Resolve LID (@lid) to phone number */
function resolvePhone(from: string): string {
  if (!from) return 'Desconhecido';
  const cleaned = from.split('@')[0].split(':')[0];
  return cleaned || from;
}

export class GrowhatsWebphone extends EventEmitter<WebphoneEvents> {
  private config: Required<Pick<WebphoneConfig, 'serverUrl' | 'theme' | 'autoReconnect' | 'accountId' | 'inboxId'>> & WebphoneConfig;
  private socket: Socket | null = null;
  private audioBridge: AudioBridge | null = null;
  private _activeCall: ManagedCallActive | null = null;
  private _outgoingCall: ManagedCallOutgoing | null = null;
  private offers: ManagedOffer[] = [];
  private destroyed = false;

  // Devices (mapped from inboxes)
  private devices: ManagedDevice[] = [];

  // Notifications
  private _notifications: Notification[] = [];

  // Settings state
  private _settings = {
    showNotifications: true,
    showSettings: true,
    showDevices: true,
    showAddDevices: true,
    showEnableDevices: true,
    showRemoveDevices: true,
    showWidgetButton: true,
  };

  // Widget state
  private _widgetOpen = false;
  private _theme: string;
  private _position: { x: number; y: number };
  _inputValue = '';

  // Recording
  private _recordingEnabled = false;

  // Client-side per-direction gates. ANDed with the server's outbound/inbound
  // flags — both must allow for a call to go through.
  _canMakeCalls = true;
  _canReceiveCalls = true;

  // Drag-and-drop UI panels around the screen, persisted via localStorage.
  _draggable = true;

  // Agent attribution (host-system user operating this webphone)
  private _agent: AgentInfo | null = null;
  private _clientInstanceId: string;

  // The inbox currently in use. Equal to config.inboxId when the caller pinned
  // one, otherwise resolved from the server on the first outgoing call or from
  // the offer payload on an incoming call.
  private _activeInboxId: number | null = null;

  // Call config from server (loaded in connect() before the socket opens).
  // Defaults mirror Prisma @default(true) for call direction flags so a slow/failed
  // config fetch does not silently drop inbound call:offer events.
  _callConfig: { callingEnabled: boolean; outboundEnabled: boolean; inboundEnabled: boolean; recordingEnabled: boolean } = {
    callingEnabled: true, outboundEnabled: true, inboundEnabled: true, recordingEnabled: false,
  };
  private _callConfigLoaded = false;

  // Lifecycle callbacks (set by UI module)
  _onRender: ((api: WebphoneAPI) => void) | null = null;
  _onDestroy: (() => void) | null = null;
  // Widget update callback (set by UI module)
  _onUpdate: (() => void) | null = null;

  // beforeunload handler reference (for cleanup)
  private _beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  constructor(config: WebphoneConfig) {
    super();
    // Defensive normalization — it's easy for integrators to paste a URL that
    // already contains an API path prefix, or to pass IDs as strings.
    // Strip trailing slashes and any repeated API path suffix (e.g. if the caller
    // pasted "https://host/api/v1/accounts/api/v1/accounts/"). The SDK always
    // appends the path itself, so we want the bare origin.
    let serverUrl = String(config.serverUrl || '').trim();
    const suffixRegex = /(\/+|\/api(\/v1(\/accounts)?)?)$/;
    while (suffixRegex.test(serverUrl)) {
      const next = serverUrl.replace(suffixRegex, '');
      if (next === serverUrl) break;
      serverUrl = next;
    }
    const accountId = Number(config.accountId);
    if (!serverUrl) throw new Error('[Webphone] serverUrl is required (ex: "https://api.seudominio.com")');
    if (!Number.isFinite(accountId) || accountId <= 0) throw new Error(`[Webphone] accountId deve ser um numero positivo, recebido: ${JSON.stringify(config.accountId)}`);

    // inboxId is optional — when omitted, the server picks any active inbox at
    // call time. When provided it must be a positive integer.
    let inboxId: number | undefined;
    if (config.inboxId != null && config.inboxId !== ('' as any)) {
      inboxId = Number(config.inboxId);
      if (!Number.isFinite(inboxId) || inboxId <= 0) {
        throw new Error(`[Webphone] inboxId deve ser um numero positivo quando informado, recebido: ${JSON.stringify(config.inboxId)}`);
      }
    }

    this.config = {
      theme: 'dark',
      autoReconnect: true,
      showWidgetButton: true,
      startOpen: false,
      ...config,
      serverUrl,
      accountId,
      inboxId: inboxId as number, // may be undefined at runtime
    };
    this._activeInboxId = inboxId ?? null;
    this._theme = this.config.theme!;
    this._widgetOpen = this.config.startOpen ?? false;
    this._position = this.resolvePosition(config.position ?? 'bottom-right');
    this._recordingEnabled = config.recording?.enabled ?? false;
    this._canMakeCalls = config.calls?.canMakeCalls ?? true;
    this._canReceiveCalls = config.calls?.canReceiveCalls ?? true;
    this._draggable = config.draggable ?? true;
    this._agent = config.agent ?? null;
    this._clientInstanceId = resolveClientInstanceId(this._agent);
    if (this._agent) {
      this._agent = { ...this._agent, clientInstanceId: this._clientInstanceId };
    }

    if (config.settingsMenu) {
      Object.assign(this._settings, config.settingsMenu);
    }
    if (config.showWidgetButton !== undefined) {
      this._settings.showWidgetButton = config.showWidgetButton;
    }

    // Create default device from inboxId only when a specific one was pinned.
    // When omitted, we defer device creation until the server picks one.
    if (inboxId != null) {
      this.devices.push(new ManagedDevice(String(inboxId)));
    }

    // Register beforeunload to salvage recordings on F5 / tab close
    if (this._recordingEnabled) {
      this._beforeUnloadHandler = () => this.emergencyFlushRecordings();
      window.addEventListener('beforeunload', this._beforeUnloadHandler);
    }
  }

  /** QR code channel API path prefix for a specific inbox. */
  private qrPathFor(inboxId: number): string {
    return `/api/v1/accounts/${this.config.accountId}/whatsapp/qrcode/${inboxId}`;
  }

  /** Prefer the active inbox (set after first outgoing/incoming call) over the pinned config value. */
  private get currentInboxId(): number | null {
    return this._activeInboxId ?? (this.config.inboxId as number | undefined) ?? null;
  }

  /**
   * Inbound path: `call:ringing` = preview (modal, accept disabled);
   * `call:offer` = VoIP keys ready (accept enabled). Same callId upgrades in place.
   */
  private ingestIncomingCall(
    data: { callId: string; from: string; isVideo?: boolean; isGroup?: boolean; inboxId?: number },
    answerable: boolean,
  ): void {
    if (!data.callId) {
      console.warn('[Webphone][Calls] evento sem callId — ignorado');
      return;
    }
    if (this._callConfigLoaded && !this._callConfig.inboundEnabled) {
      console.warn('[Webphone][Calls] inbound ignorado — inboundEnabled=false na conta');
      return;
    }
    if (!this._canReceiveCalls) {
      console.warn('[Webphone][Calls] inbound ignorado — canReceiveCalls=false no SDK');
      return;
    }
    if (data.inboxId) this._activeInboxId = data.inboxId;

    const phone = resolvePhone(data.from);
    const existing = this.offers.find((o) => o.call_id === data.callId);
    if (existing) {
      if (answerable) {
        existing.answerable = true;
        console.info('[Webphone][Calls] oferta atualizada (agora atendível)', data.callId);
        this.emit('call:offer:ready', existing);
        this._onUpdate?.();
      }
      return;
    }

    const offer = new ManagedOffer(this, data.callId, phone, answerable);
    this.offers.push(offer);
    console.info('[Webphone][Calls] nova oferta', { callId: data.callId, answerable, from: phone });
    this.emit('call:incoming', offer);
    this._onUpdate?.();
  }

  /** Calls config API path */
  private get callsConfigPath(): string {
    return `/api/v1/accounts/${this.config.accountId}/calls/config`;
  }

  // ─── Connection ───

  async connect(): Promise<void> {
    await this.loadCallConfig();
    if (this.socket) {
      if (!this.socket.connected) this.socket.connect();
      return;
    }
    this.connectSocket();
  }

  /** Fetch /calls/config before opening the socket so inbound gating is accurate. */
  private async loadCallConfig(): Promise<void> {
    try {
      const headers: Record<string, string> = {};
      if (this.config.apiKey) {
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
        headers['X-Api-Key'] = this.config.apiKey;
      }
      const res = await fetch(`${this.config.serverUrl}${this.callsConfigPath}`, { headers, signal: AbortSignal.timeout(5000) });
      if (res.ok) {
        const data = await res.json();
        if (data?.data) {
          this._callConfig = {
            callingEnabled: data.data.callingEnabled ?? true,
            outboundEnabled: data.data.outboundEnabled ?? true,
            inboundEnabled: data.data.inboundEnabled ?? true,
            recordingEnabled: data.data.recordingEnabled ?? false,
          };
          this._callConfigLoaded = true;
          if (this._callConfig.recordingEnabled) {
            this._recordingEnabled = true;
          }
        }
      } else {
        console.warn(`[Webphone] Config fetch HTTP ${res.status} — usando defaults (inbound=${this._callConfig.inboundEnabled})`);
      }
      if (this.devices[0]) this.devices[0]._setStatus('open');
    } catch (err: any) {
      console.warn('[Webphone] Config fetch failed:', err.message, '— inbound offers use defaults until retry');
    }
  }

  private connectSocket(): void {
    if (this.destroyed) return;

    const socketUrl = resolveSocketUrl(
      this.config.serverUrl,
      this.config.socketUrl,
      this.config.socketViaApiProxy !== false,
    );
    const pinnedInboxId = this.config.inboxId as number | undefined;
    this.socket = io(socketUrl, {
      auth: {
        token: this.config.apiKey || '',
        clientInstanceId: this._clientInstanceId,
        // Top-level inboxId is read by the server when no agent object is sent.
        ...(pinnedInboxId ? { inboxId: pinnedInboxId } : {}),
        agent: this._agent
          ? { ...this._agent, clientInstanceId: this._clientInstanceId, ...(pinnedInboxId ? { inboxId: pinnedInboxId } : {}) }
          : undefined,
      },
      transports: ['websocket', 'polling'],
      reconnection: this.config.autoReconnect,
      reconnectionDelay: 3000,
    });

    this.socket.on('connect', () => {
      console.info('[Webphone][Calls] Socket conectado', {
        socketUrl,
        accountId: this.config.accountId,
        inboxId: pinnedInboxId ?? this._activeInboxId,
        clientInstanceId: this._clientInstanceId,
        agent: this._agent?.idExternal,
      });
      this.emit('connection:status', true);
    });

    this.socket.on('disconnect', (reason) => {
      console.info('[Webphone][Calls] Socket desconectado:', reason);
      this.emit('connection:status', false);
    });

    this.socket.on('connect_error', (err) => {
      console.warn('[Webphone][Calls] Erro de conexão socket:', (err as Error)?.message ?? err);
      this.emit('connection:status', false);
    });

    const callPayload = (data: { callId: string; from: string; isVideo?: boolean; isGroup?: boolean; inboxId?: number }) => ({
      callId: data.callId,
      from: data.from,
      inboxId: data.inboxId,
      isVideo: data.isVideo,
      isGroup: data.isGroup,
    });

    this.socket.on('call:ringing', (data: { callId: string; from: string; isVideo?: boolean; isGroup?: boolean; inboxId?: number }) => {
      console.info('[Webphone][Calls] call:ringing', callPayload(data));
      this.ingestIncomingCall(data, false);
    });

    this.socket.on('call:offer', (data: { callId: string; from: string; isVideo?: boolean; isGroup?: boolean; inboxId?: number }) => {
      console.info('[Webphone][Calls] call:offer', callPayload(data));
      this.ingestIncomingCall(data, true);
    });

    // Peer accepted an outgoing call — transition from "Chamando" to in-call.
    // Setting the status to ACTIVE fires the peerAcceptCbs registered on the
    // ManagedCallOutgoing via its onPeerAccept() method, which is how the
    // widget (and any custom dialer) knows to stop the ringback + start the
    // duration timer. Incoming accepts are handled via offer.accept() instead.
    this.socket.on('call:accept', (data: { callId: string; inboxId?: number }) => {
      const callId = data?.callId;
      if (!callId) return;
      if (this._outgoingCall && this._outgoingCall.id === callId) {
        this._outgoingCall._setStatus('ACTIVE');
        this.emit('call:status', callId, 'ACTIVE');
      } else if (this._activeCall && this._activeCall.id === callId) {
        this.emit('call:status', callId, 'ACTIVE');
      }
    });

    this.socket.on('call:terminate', (data: { callId: string; reason?: string }) => {
      console.info('[Webphone][Calls] call:terminate', { callId: data.callId, reason: data.reason });
      const callId = data.callId;
      // We owned audio for this call if either an incoming was accepted (`_activeCall`)
      // or an outgoing was placed (`_outgoingCall`). In both cases connectAudio() ran,
      // so we must tear it down — otherwise mic stays open and the WS keeps streaming
      // remote frames into the speakers ("barulho de pulso").
      const ownedAudio =
        !!(this._activeCall && this._activeCall.id === callId) ||
        !!(this._outgoingCall && this._outgoingCall.id === callId);
      this.emit('call:ended', callId, data.reason || 'ended');
      if (this._activeCall && this._activeCall.id === callId) {
        this._activeCall._setStatus('ENDED');
        this._activeCall = null;
      }
      if (this._outgoingCall && this._outgoingCall.id === callId) {
        this._outgoingCall._setStatus('ENDED');
        this._outgoingCall = null;
      }
      const hadOffer = this.offers.some(o => o.call_id === callId);
      this.offers = this.offers.filter(o => o.call_id !== callId);
      // Re-render any host UI listening on _onUpdate (widget, host dashboard) so
      // the incoming modal disappears for agents who had this offer queued.
      if (hadOffer) this._onUpdate?.();
      // Detach the audio bridge SYNCHRONOUSLY before kicking off async cleanup —
      // otherwise a quick second call would see `this.audioBridge` still set
      // (because disconnectAudio's `= null` only runs at the end of an async
      // chain that takes seconds while the recording uploads), and connectAudio
      // would skip creating a fresh bridge and try to reuse the dying one.
      // That's exactly the "second call doesn't work" symptom.
      const bridgeForCleanup = ownedAudio ? this.audioBridge : null;
      if (ownedAudio) this.audioBridge = null;
      // Stop+upload recording first, THEN tear down the bridge. Doing both in
      // parallel races: disconnectAudio re-enters stopAndUploadRecording, finds
      // recorder.state === 'inactive' (stop() flips state synchronously), deletes
      // the session from the map and the first promise's onstop never fires —
      // so the upload + recording:stopped event are silently dropped.
      (async () => {
        if (bridgeForCleanup) {
          try { await this.stopAndUploadRecordingOn(bridgeForCleanup, callId); } catch { /* swallow */ }
          try { bridgeForCleanup.disconnect(); } catch { /* */ }
        }
      })();
    });

    // ── Multi-agent coordination ──

    this.socket.on('call:taken', (data: CallTakenEvent) => {
      const myId = this._agent?.idExternal;
      const takenInst = data.takenBy?.clientInstanceId;
      // Same CRM user on another tab answered — still dismiss our offer.
      if (
        myId
        && data.takenBy?.idExternal === myId
        && (!takenInst || takenInst === this._clientInstanceId)
      ) {
        return;
      }
      this.offers = this.offers.filter(o => o.call_id !== data.callId);
      this.emit('call:taken', data);
      this._onUpdate?.();
    });

    this.socket.on('agent:duplicate_session', (data: AgentDuplicateSessionEvent) => {
      console.warn(
        '[Webphone] Mesmo idExternal em outra aba/sessão:',
        data.idExternal,
        'existentes:',
        data.existing?.map((e) => e.clientInstanceId).join(', '),
      );
      this.emit('agent:duplicate_session', data);
    });

    this.socket.on('call:missed', (data: { callId: string; from: string; direction: 'incoming' | 'outgoing'; inboxId?: number; timestamp?: number }) => {
      // Emitted by the server when a call ends without ever being accepted
      // (no-answer timeout, peer reject of our outgoing, our own cancel of an
      // outgoing before peer answered, etc.). Surfaces as a distinct event
      // so the host UI / widget can show a "Não atendida" badge instead of
      // a generic "encerrada".
      this.emit('call:missed', data);
    });

    this.socket.on('call:claim:denied', (data: CallTakenEvent) => {
      this.offers = this.offers.filter(o => o.call_id !== data.callId);
      this.emit('call:claim:denied', data);
      this._onUpdate?.();
    });

    this.socket.on('agent:joined', (data: { agent: { idExternal: string; name?: string; email?: string; role?: string }; inboxId?: number; socketId: string }) => {
      this.emit('agent:joined', {
        idExternal: data.agent.idExternal,
        name: data.agent.name,
        email: data.agent.email,
        role: data.agent.role,
        socketId: data.socketId,
        inboxId: data.inboxId,
        since: Date.now(),
      });
    });

    this.socket.on('agent:left', (data: { agent: { idExternal: string; name?: string; email?: string; role?: string }; inboxId?: number; socketId: string }) => {
      this.emit('agent:left', {
        idExternal: data.agent.idExternal,
        name: data.agent.name,
        email: data.agent.email,
        role: data.agent.role,
        socketId: data.socketId,
        inboxId: data.inboxId,
        since: Date.now(),
      });
    });
  }

  // ─── HTTP API ───

  private async api<T = any>(method: string, path: string, body?: any): Promise<T> {
    const url = `${this.config.serverUrl}${path}`;
    const headers: Record<string, string> = {};
    if (body) headers['Content-Type'] = 'application/json';
    if (this.config.apiKey) headers['Authorization'] = `Bearer ${this.config.apiKey}`;
    const res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
    if (!res.ok) {
      const text = await res.text();
      let parsed: any = null;
      try { parsed = JSON.parse(text); } catch { /* not JSON */ }
      const err = new Error(parsed?.error?.message || `API ${method} ${path} failed (${res.status}): ${text}`) as Error & { status?: number; code?: string; takenBy?: any };
      err.status = res.status;
      err.code = parsed?.error?.code;
      err.takenBy = parsed?.takenBy;
      throw err;
    }
    return res.json();
  }

  /** Serialize the current agent for HTTP bodies. Returns `undefined` when no agent set. */
  private agentBody(): AgentInfo | undefined {
    if (!this._agent) return undefined;
    // Strip undefined keys so the server zod schema stays happy
    const a: AgentInfo = {
      idExternal: this._agent.idExternal,
      clientInstanceId: this._clientInstanceId,
    };
    if (this._agent.name) a.name = this._agent.name;
    if (this._agent.email) a.email = this._agent.email;
    if (this._agent.role) a.role = this._agent.role;
    if (this._agent.metadata) a.metadata = this._agent.metadata;
    return a;
  }

  // ─── Call management (via QR code channel routes) ───

  async startCall(to: string, config?: { fromTokens?: string[]; displayName?: string }): Promise<ManagedCallOutgoing> {
    if (!this._canMakeCalls) {
      throw new Error('[Webphone] Este usuário não pode fazer chamadas (calls.canMakeCalls = false)');
    }
    const body: any = { phone: to };
    const agent = this.agentBody();
    if (agent) body.agent = agent;

    const pinnedInboxId = (this.config.inboxId as number | undefined);
    const url = pinnedInboxId
      ? `${this.qrPathFor(pinnedInboxId)}/calls`
      : `/api/v1/accounts/${this.config.accountId}/whatsapp/qrcode/calls`;

    console.info('[Webphone][Calls] startCall', { to, inboxId: pinnedInboxId });
    let data: any;
    try {
      data = await this.api<any>('POST', url, body);
    } catch (err) {
      const e = err as Error & { status?: number; code?: string };
      console.error('[Webphone][Calls] startCall HTTP failure', { status: e.status, code: e.code, message: e.message });
      throw err;
    }
    const callId = data?.data?.callId || data?.callId || 'unknown';
    const resolvedInboxId: number | undefined = data?.data?.inboxId ?? pinnedInboxId;
    if (resolvedInboxId) this._activeInboxId = resolvedInboxId;

    const call = new ManagedCallOutgoing(this, callId, to, String(resolvedInboxId ?? ''), config?.displayName ?? null);
    this._outgoingCall = call;
    this.emit('call:started', call as any);

    // Local safety net: if the server never confirms the peer accepted (or
    // never sends a terminate webhook for an unreachable peer), kill the
    // local "Chamando..." state after 60s so the UI doesn't ring forever.
    // The server-side timeout is 45s; this guards against socket drops too.
    this.startOutgoingRingGuard(callId, 60_000);

    await this.connectAudio(callId);
    return call;
  }

  private startOutgoingRingGuard(callId: string, ms: number): void {
    setTimeout(() => {
      if (!this._outgoingCall || this._outgoingCall.id !== callId) return;
      if (this._outgoingCall.status === 'ACTIVE') return;
      console.warn('[Webphone][Calls] outgoing ring guard tripped — no peer accept in', ms, 'ms');
      this.emit('call:missed', { callId, from: this._outgoingCall.phone, direction: 'outgoing', timestamp: Date.now() });
      this.emit('call:ended', callId, 'no_answer');
      const out = this._outgoingCall;
      this._outgoingCall = null;
      try { out._setStatus('ENDED'); } catch { /* */ }
      // Tell the server to drop the call so the WASM engine is released.
      const inboxId = this.currentInboxId;
      if (inboxId) {
        this.api('POST', `${this.qrPathFor(inboxId)}/calls/${callId}/hangup`).catch(() => { /* swallow */ });
      }
    }, ms);
  }

  async acceptCall(callId: string): Promise<ManagedCallActive> {
    const inboxId = this.currentInboxId;
    if (!inboxId) throw new Error('[Webphone] Nenhum inboxId ativo — aceite após receber uma call:offer');
    const offer = this.offers.find((o) => o.call_id === callId);
    if (offer && offer.answerable === false) {
      throw new Error('[Webphone] Chamada ainda não está pronta para atender — aguarde call:offer');
    }
    const agent = this.agentBody();
    try {
      await this.api('POST', `${this.qrPathFor(inboxId)}/calls/${callId}/accept`, agent ? { agent } : undefined);
    } catch (err) {
      // 409 = another agent on this inbox already claimed the call.
      const e = err as Error & { status?: number; code?: string; takenBy?: any };
      if (e.status === 409 && e.code === 'CALL_ALREADY_TAKEN') {
        this.offers = this.offers.filter(o => o.call_id !== callId);
        this.emit('call:claim:denied', { callId, takenBy: e.takenBy || { idExternal: 'unknown', claimedAt: Date.now() } });
        this._onUpdate?.();
      }
      throw err;
    }
    const phone = offer?.from || 'unknown';
    this.offers = this.offers.filter(o => o.call_id !== callId);

    const call = new ManagedCallActive(this, callId, phone, 'INCOMING', String(inboxId));
    call._setStatus('ACTIVE');
    call._connectionStatus = 'connected';
    this._activeCall = call;
    this.emit('call:started', call);
    await this.connectAudio(callId);
    return call;
  }

  async rejectCall(callId: string): Promise<{ err: string | null }> {
    const inboxId = this.currentInboxId;
    if (!inboxId) return { err: 'No active inbox' };
    try {
      await this.api('POST', `${this.qrPathFor(inboxId)}/calls/${callId}/reject`);
      // Update local state immediately — the server emits `call:terminate` to
      // peers asynchronously, but the agent who clicked Reject must see their
      // own UI clear right away.
      this.offers = this.offers.filter(o => o.call_id !== callId);
      this.emit('call:ended', callId, 'rejected');
      this._onUpdate?.();
      return { err: null };
    } catch (e: any) {
      return { err: e.message };
    }
  }

  async endCall(callId?: string): Promise<{ err: string | null }> {
    const id = callId || this._activeCall?.id || this._outgoingCall?.id;
    if (!id) return { err: 'No active call' };
    const inboxId = this.currentInboxId;
    if (!inboxId) return { err: 'No active inbox' };

    // SYNCHRONOUS state teardown — must happen before any await, so if the user
    // starts a second call while the recording is still uploading, the new call
    // sees a clean slate. Previously the bridge stayed on `this.audioBridge`
    // during the upload window, and a late `disconnectAudio()` would grab the
    // NEW bridge and kill the new call's audio.
    const bridge = this.audioBridge;
    this.audioBridge = null;
    if (this._activeCall?.id === id) { this._activeCall._setStatus('ENDED'); this._activeCall = null; }
    if (this._outgoingCall?.id === id) { this._outgoingCall._setStatus('ENDED'); this._outgoingCall = null; }

    // Async cleanup of the captured bridge runs in the background — its work
    // (recording upload, then disconnect) never touches `this.audioBridge` so
    // it can't interfere with a concurrent new call.
    if (bridge) {
      (async () => {
        try { await this.stopAndUploadRecordingOn(bridge, id); } catch { /* swallow */ }
        try { bridge.disconnect(); } catch { /* */ }
      })();
    }

    try {
      await this.api('POST', `${this.qrPathFor(inboxId)}/calls/${id}/hangup`);
      return { err: null };
    } catch (e: any) {
      return { err: e.message };
    }
  }

  // ─── Audio (WebSocket via QR code channel route) ───

  private async connectAudio(callId: string): Promise<void> {
    // Always start a new call with a fresh bridge. If something failed to clean
    // up (e.g. a previous error path), fire-and-forget the old one so we never
    // try to mount a new WS / mic capture on a half-dead bridge — that was the
    // root cause of "second call doesn't work without reconnect".
    if (this.audioBridge) {
      const stale = this.audioBridge;
      this.audioBridge = null;
      try { stale.disconnect(); } catch { /* */ }
    }
    this.audioBridge = new AudioBridge();

    try {
      // Build WebSocket URL directly (same as dashboard)
      const inboxId = this.currentInboxId;
      if (!inboxId) throw new Error('[Webphone] Nenhum inbox ativo para conectar audio');
      const wsProtocol = this.config.serverUrl.startsWith('https') ? 'wss:' : 'ws:';
      const host = new URL(this.config.serverUrl).host;
      const token = encodeURIComponent(this.config.apiKey || '');
      const wsUrl = `${wsProtocol}//${host}${this.qrPathFor(inboxId)}/calls/${callId}/ws?token=${token}`;

      await this.audioBridge.connect(wsUrl);

      if (this._recordingEnabled) {
        const started = this.audioBridge.startRecording(callId);
        if (started) {
          this.emit('recording:started', callId);
        } else {
          // Recording was supposed to happen but the bridge couldn't kick it
          // off — usually means the browser doesn't support MediaRecorder or
          // the mic stream wasn't acquired. Surface this so the host UI can
          // warn the user instead of silently failing to capture the call.
          console.warn(`[Webphone] startRecording returned false for call ${callId}`);
          this.emit('error', new Error('Não foi possível iniciar a gravação (MediaRecorder indisponível ou microfone não capturado).'));
        }
      }
    } catch (err: any) {
      console.error('[Webphone] Audio connection failed:', err.message);
      this.emit('error', new Error(`Falha na conexão de áudio: ${err.message}`));
    }
  }

  private async stopAndUploadRecording(callId: string): Promise<void> {
    if (!this.audioBridge) return;
    return this.stopAndUploadRecordingOn(this.audioBridge, callId);
  }

  /**
   * Stop+upload using an explicit bridge instance. Used by the call:terminate
   * handler, which detaches `this.audioBridge` synchronously to avoid races
   * with a quickly-started second call, then drains the old bridge here.
   */
  private async stopAndUploadRecordingOn(bridge: AudioBridge, callId: string): Promise<void> {
    if (!bridge.isRecording(callId)) return;
    try {
      const result = await bridge.stopRecording(callId);
      if (result && result.blob.size > 0) {
        const url = await this.uploadRecordingBlob(callId, result.blob, result.durationSeconds, result.startedAt, result.endedAt);
        this.emit('recording:stopped', callId, { url, callId, durationSeconds: result.durationSeconds, startedAt: result.startedAt, endedAt: result.endedAt });
      } else {
        this.emit('recording:stopped', callId, null);
      }
    } catch {
      this.emit('recording:stopped', callId, null);
    }
  }

  private async disconnectAudio(): Promise<void> {
    if (!this.audioBridge) return;
    // Detach synchronously so any concurrent connectAudio (e.g. user starting
    // a second call) sees `this.audioBridge === null` and creates a fresh
    // instance instead of reusing the one we're tearing down.
    const bridge = this.audioBridge;
    this.audioBridge = null;
    const activeIds = bridge.getActiveRecordingIds();
    for (const callId of activeIds) {
      await this.stopAndUploadRecordingOn(bridge, callId);
    }
    try { bridge.disconnect(); } catch { /* */ }
  }

  muteAudio(): { err: string | null } {
    this.audioBridge?.mute();
    if (this._activeCall) this._activeCall._muted = true;
    if (this._outgoingCall) (this._outgoingCall as any)._muted = true;
    return { err: null };
  }

  unmuteAudio(): { err: string | null } {
    this.audioBridge?.unmute();
    if (this._activeCall) this._activeCall._muted = false;
    if (this._outgoingCall) (this._outgoingCall as any)._muted = false;
    return { err: null };
  }

  /** Get the AudioBridge analyser for audio level visualization */
  getAudioAnalyser(): AnalyserNode | null {
    return this.audioBridge?.getAnalyser() ?? null;
  }

  // ─── Recording (public) ───

  get recordingEnabled(): boolean { return this._recordingEnabled; }
  isRecording(callId?: string): boolean { return this.audioBridge?.isRecording(callId) ?? false; }
  getActiveRecordingIds(): string[] { return this.audioBridge?.getActiveRecordingIds() ?? []; }

  async getRecordingUrl(callId: string): Promise<string | null> {
    try {
      const data = await this.api<any>('GET', `/api/v1/accounts/${this.config.accountId}/calls/${callId}`);
      return data?.data?.recordingUrl || null;
    } catch { return null; }
  }

  private emergencyFlushRecordings(): void {
    if (!this.audioBridge) return;
    const flushed = this.audioBridge.flushAllRecordings();
    for (const [callId, data] of flushed) {
      if (data.blob.size === 0) continue;
      const formData = new FormData();
      formData.append('file', data.blob, `${callId}.webm`);
      formData.append('callId', callId);
      formData.append('duration', String(data.durationSeconds));
      formData.append('startedAt', data.startedAt);
      formData.append('endedAt', data.endedAt);
      formData.append('emergency', 'true');
      if (this._agent) {
        formData.append('agentIdExternal', this._agent.idExternal);
        if (this._agent.name) formData.append('agentName', this._agent.name);
        if (this._agent.email) formData.append('agentEmail', this._agent.email);
      }
      const url = `${this.config.serverUrl}/api/v1/accounts/${this.config.accountId}/calls/${callId}/recording`;
      try {
        // Build URL with apiKey in query string since sendBeacon doesn't support custom headers
        const beaconUrl = new URL(url);
        if (this.config.apiKey) beaconUrl.searchParams.append('apiKey', this.config.apiKey);
        navigator.sendBeacon(beaconUrl.toString(), formData);
      } catch { /* */ }
      this.emit('recording:stopped', callId, { callId, url, durationSeconds: data.durationSeconds, startedAt: data.startedAt, endedAt: data.endedAt });
    }
  }

  private async uploadRecordingBlob(callId: string, blob: Blob, durationSeconds: number, startedAt: string, endedAt: string): Promise<string> {
    const url = `${this.config.serverUrl}/api/v1/accounts/${this.config.accountId}/calls/${callId}/recording`;
    const headers: Record<string, string> = {};
    if (this.config.apiKey) headers['X-Api-Key'] = this.config.apiKey;

    // Each retry needs a fresh FormData because the body stream is consumed
    // on the first fetch attempt.
    const buildBody = (): FormData => {
      const fd = new FormData();
      fd.append('file', blob, `${callId}.webm`);
      fd.append('callId', callId);
      fd.append('duration', String(durationSeconds));
      fd.append('startedAt', startedAt);
      fd.append('endedAt', endedAt);
      if (this._agent) {
        fd.append('agentIdExternal', this._agent.idExternal);
        if (this._agent.name) fd.append('agentName', this._agent.name);
        if (this._agent.email) fd.append('agentEmail', this._agent.email);
      }
      return fd;
    };

    // Retry with exponential backoff. Network blips, server restarts, or
    // 5xx responses during heavy load are common reasons a single attempt
    // fails — the recording is too valuable to drop on the first hiccup.
    const maxAttempts = 4;
    const baseDelayMs = 800;
    let lastError: Error | null = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await fetch(url, { method: 'POST', headers, body: buildBody() });
        if (res.ok) {
          const data = await res.json();
          return data?.data?.recordingUrl || '';
        }
        // 4xx is the SDK/server's fault, not transient — don't retry.
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`Upload failed: ${res.status}`);
        }
        lastError = new Error(`Upload failed: ${res.status}`);
      } catch (err: any) {
        lastError = err instanceof Error ? err : new Error(String(err));
        // Non-retryable: explicit 4xx classification above bubbles up.
        if (/Upload failed: 4\d\d/.test(lastError.message)) throw lastError;
      }
      if (attempt < maxAttempts) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        console.warn(`[Webphone] Recording upload retry ${attempt}/${maxAttempts - 1} in ${delay}ms: ${lastError?.message}`);
        await new Promise((r) => setTimeout(r, delay));
      }
    }
    throw lastError ?? new Error('Upload failed after retries');
  }

  // ─── Agent ───

  getAgent(): AgentInfo | null {
    return this._agent ? { ...this._agent } : null;
  }

  /**
   * Update the agent. Re-opens the socket so the server picks up the new identity
   * on the handshake (and so peers get agent:joined/left events for the switch).
   */
  setAgent(agent: AgentInfo | null): void {
    if (agent && !agent.idExternal) throw new Error('agent.idExternal is required');
    this._clientInstanceId = resolveClientInstanceId(agent);
    this._agent = agent ? { ...agent, clientInstanceId: this._clientInstanceId } : null;
    // Reconnect socket so the new agent is advertised in the handshake.
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connectSocket();
    }
  }

  async getOnlineAgents(): Promise<OnlineAgent[]> {
    if (!this.socket || !this.socket.connected) return [];
    const inboxId = this.currentInboxId;
    if (!inboxId) return [];
    return new Promise<OnlineAgent[]>((resolve) => {
      const timeout = setTimeout(() => resolve([]), 3000);
      this.socket!.emit('agent:list', { inboxId }, (agents: OnlineAgent[]) => {
        clearTimeout(timeout);
        resolve(Array.isArray(agents) ? agents : []);
      });
    });
  }

  // ─── Notifications ───

  addNotification(n: Partial<Notification>): void {
    this._notifications.push({
      id: n.id || new Date(), type: n.type || 'INFO', message: n.message || '',
      detail: n.detail, token: n.token, isRead: false, isHidden: false, created_at: new Date(),
    });
  }
  removeNotification(id: Date): void { this._notifications = this._notifications.filter(n => n.id !== id); }
  clearNotifications(): void { this._notifications = []; }
  readNotifications(): void { this._notifications.forEach(n => n.isRead = true); }

  // ─── Widget helpers ───

  private resolvePosition(pos: PositionPreset | { x: number; y: number }): { x: number; y: number } {
    if (typeof pos === 'object') return pos;
    const m = 20;
    switch (pos) {
      case 'top-left': return { x: m, y: m };
      case 'top-right': return { x: window.innerWidth - 80 - m, y: m };
      case 'bottom-left': return { x: m, y: window.innerHeight - 80 - m };
      case 'bottom-right': return { x: window.innerWidth - 80 - m, y: window.innerHeight - 80 - m };
      case 'center': return { x: window.innerWidth / 2 - 40, y: window.innerHeight / 2 - 40 };
      default: return { x: window.innerWidth - 100, y: window.innerHeight - 100 };
    }
  }

  // ─── Public API (Wavoip-compatible shape) ───

  getAPI(): WebphoneAPI {
    const self = this;
    return {
      call: {
        start: (to, config) => self.startCall(to, config) as any,
        startCall: (to, fromTokens) => self.startCall(to, fromTokens ? { fromTokens } : undefined) as any,
        getCallActive: () => self._activeCall || undefined,
        getCallOutgoing: () => self._outgoingCall || undefined,
        getOffers: () => [...self.offers],
        onOffer: (cb) => self.on('call:incoming', cb),
        setInput: (to) => { self._inputValue = to; },
      },
      device: {
        get: () => [...self.devices],
        getDevices: () => [...self.devices],
        add: (token) => { if (!self.devices.find(d => d.token === token)) self.devices.push(new ManagedDevice(token)); },
        addDevice: (token) => { if (!self.devices.find(d => d.token === token)) self.devices.push(new ManagedDevice(token)); },
        remove: (token) => { self.devices = self.devices.filter(d => d.token !== token); },
        removeDevice: (token) => { self.devices = self.devices.filter(d => d.token !== token); },
        enable: (token) => { self.devices.find(d => d.token === token)?._setStatus('open'); },
        enableDevice: (token) => { self.devices.find(d => d.token === token)?._setStatus('open'); },
        disable: (token) => { self.devices.find(d => d.token === token)?._setStatus('disconnected'); },
        disableDevice: (token) => { self.devices.find(d => d.token === token)?._setStatus('disconnected'); },
      },
      notifications: {
        get: () => [...self._notifications],
        getNotifications: () => [...self._notifications],
        add: (n) => self.addNotification(n),
        addNotification: (n) => self.addNotification(n),
        remove: (id) => self.removeNotification(id),
        removeNotification: (id) => self.removeNotification(id),
        clear: () => self.clearNotifications(),
        clearNotifications: () => self.clearNotifications(),
        read: () => self.readNotifications(),
        readNotifications: () => self.readNotifications(),
      },
      widget: {
        get isOpen() { return self._widgetOpen; },
        open: () => { self._widgetOpen = true; },
        close: () => { self._widgetOpen = false; },
        toggle: () => { self._widgetOpen = !self._widgetOpen; },
        buttonPosition: {
          get value() { return self._position; },
          set: (p) => { self._position = self.resolvePosition(p); },
        },
      },
      theme: {
        get value() { return self._theme; },
        set: (t) => { self._theme = t; },
        setTheme: (t) => { self._theme = t; },
      },
      position: {
        get value() { return self._position; },
        set: (p) => { self._position = self.resolvePosition(p); },
      },
      settings: {
        get showNotifications() { return self._settings.showNotifications; },
        setShowNotifications: (v) => { self._settings.showNotifications = v; },
        get showSettings() { return self._settings.showSettings; },
        setShowSettings: (v) => { self._settings.showSettings = v; },
        get showDevices() { return self._settings.showDevices; },
        setShowDevices: (v) => { self._settings.showDevices = v; },
        get showAddDevices() { return self._settings.showAddDevices; },
        setShowAddDevices: (v) => { self._settings.showAddDevices = v; },
        get showEnableDevices() { return self._settings.showEnableDevices; },
        setShowEnableDevices: (v) => { self._settings.showEnableDevices = v; },
        get showRemoveDevices() { return self._settings.showRemoveDevices; },
        setShowRemoveDevices: (v) => { self._settings.showRemoveDevices = v; },
        get showWidgetButton() { return self._settings.showWidgetButton; },
        setShowWidgetButton: (v) => { self._settings.showWidgetButton = v; },
      },
      recording: {
        get enabled() { return self._recordingEnabled; },
        getActiveRecordings: () => self.getActiveRecordingIds(),
        isRecording: (callId?) => self.isRecording(callId),
        getUrl: (callId) => self.getRecordingUrl(callId),
      },
      agent: {
        get: () => self.getAgent(),
        set: (agent) => self.setAgent(agent),
        getOnlineAgents: () => self.getOnlineAgents(),
      },
      on: (event, cb) => self.on(event, cb),
      off: (event, cb) => self.off(event, cb),
      destroy: () => self.destroy(),
    };
  }

  // ─── Lifecycle ───

  destroy(): void {
    this.destroyed = true;
    if (this._beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this._beforeUnloadHandler);
      this._beforeUnloadHandler = null;
    }
    this.socket?.disconnect();
    this.socket = null;
    this.disconnectAudio();
    this.removeAll();
    this._onDestroy?.();
  }
}

// ─── Internal: ManagedDevice ───

class ManagedDevice implements Device {
  private _status: DeviceStatus = 'disconnected';
  private _qrcode?: string;
  private _contact?: { phone?: string; name?: string };
  private statusCbs: Array<(s: DeviceStatus) => void> = [];
  private qrCbs: Array<(qr: string) => void> = [];
  private contactCbs: Array<(c: { phone?: string; name?: string }) => void> = [];

  constructor(public token: string) {}
  get status() { return this._status; }
  get qrcode() { return this._qrcode; }
  get contact() { return this._contact; }

  _setStatus(s: DeviceStatus) { this._status = s; this.statusCbs.forEach(cb => cb(s)); }
  _setQRCode(qr: string) { this._qrcode = qr; this.qrCbs.forEach(cb => cb(qr)); }
  _setContact(c: { phone?: string; name?: string }) { this._contact = c; this.contactCbs.forEach(cb => cb(c)); }

  onStatus(cb: (status: DeviceStatus) => void) { this.statusCbs.push(cb); }
  onQRCode(cb: (qr: string) => void) { this.qrCbs.push(cb); }
  onContact(cb: (contact: { phone?: string; name?: string }) => void) { this.contactCbs.push(cb); }
}

// ─── Internal: ManagedOffer ───

class ManagedOffer implements CallOffer {
  private endCbs: Array<() => void> = [];
  private statusCbs: Array<(s: CallStatus) => void> = [];
  private acceptElsewhereCbs: Array<() => void> = [];
  private rejectElsewhereCbs: Array<() => void> = [];
  private unansweredCbs: Array<() => void> = [];

  /** False until `call:offer` (VoIP keys). True when only legacy servers send offer directly. */
  answerable: boolean;

  constructor(
    private webphone: GrowhatsWebphone,
    public call_id: string,
    public from: string,
    answerable = true,
    public displayName?: string,
  ) {
    this.answerable = answerable;
  }

  async accept(): Promise<CallActive> { return this.webphone.acceptCall(this.call_id); }
  async reject(): Promise<{ err: string | null }> { return this.webphone.rejectCall(this.call_id); }

  onAcceptedElsewhere(cb: () => void) { this.acceptElsewhereCbs.push(cb); }
  onRejectedElsewhere(cb: () => void) { this.rejectElsewhereCbs.push(cb); }
  onUnanswered(cb: () => void) { this.unansweredCbs.push(cb); }
  onEnd(cb: () => void) { this.endCbs.push(cb); }
  onStatus(cb: (status: CallStatus) => void) { this.statusCbs.push(cb); }
}

// ─── Internal: ManagedCallActive ───

class ManagedCallActive implements CallActive {
  _muted = false;
  private _status: CallStatus = 'RINGING';
  private _duration = 0;
  private durationTimer: ReturnType<typeof setInterval> | null = null;
  _connectionStatus: 'disconnected' | 'connected' | 'connecting' = 'connecting';

  private statusCbs: Array<(s: CallStatus) => void> = [];
  private endCbs: Array<() => void> = [];
  private errorCbs: Array<(err: string) => void> = [];
  private peerMuteCbs: Array<() => void> = [];
  private peerUnmuteCbs: Array<() => void> = [];
  private statsCbs: Array<(stats: CallStats) => void> = [];
  private connStatusCbs: Array<(s: string) => void> = [];

  type: 'official' = 'official';
  direction: CallDirection;
  peer: { phone: string; displayName: string | null; profilePicture: string | null; muted: boolean };

  constructor(private webphone: GrowhatsWebphone, public id: string, public phone: string, direction: CallDirection, public device_token: string) {
    this.direction = direction;
    this.peer = { phone, displayName: null, profilePicture: null, muted: false };
    this.durationTimer = setInterval(() => { if (this._status === 'ACTIVE') this._duration++; }, 1000);
  }

  get status() { return this._status; }
  get muted() { return this._muted; }
  get connection_status() { return this._connectionStatus; }
  get audio_analyser(): Promise<AnalyserNode | null> { return Promise.resolve(this.webphone.getAudioAnalyser()); }

  _setStatus(s: CallStatus) {
    this._status = s;
    this.statusCbs.forEach(cb => cb(s));
    if (s === 'ENDED' || s === 'FAILED' || s === 'DISCONNECTED') {
      this.endCbs.forEach(cb => cb());
      if (this.durationTimer) clearInterval(this.durationTimer);
    }
  }

  async mute(): Promise<{ err: string | null }> { return this.webphone.muteAudio(); }
  async unmute(): Promise<{ err: string | null }> { return this.webphone.unmuteAudio(); }
  async end(): Promise<{ err: string | null }> { return this.webphone.endCall(this.id); }

  onError(cb: (err: string) => void) { this.errorCbs.push(cb); }
  onPeerMute(cb: () => void) { this.peerMuteCbs.push(cb); }
  onPeerUnmute(cb: () => void) { this.peerUnmuteCbs.push(cb); }
  onEnd(cb: () => void) { this.endCbs.push(cb); }
  onStats(cb: (stats: CallStats) => void) { this.statsCbs.push(cb); }
  onConnectionStatus(cb: (status: string) => void) { this.connStatusCbs.push(cb); }
  onStatus(cb: (status: CallStatus) => void) { this.statusCbs.push(cb); }
}

// ─── Internal: ManagedCallOutgoing ───

class ManagedCallOutgoing implements CallOutgoing {
  _muted = false;
  private _status: CallStatus = 'CALLING';
  private durationTimer: ReturnType<typeof setInterval> | null = null;
  _connectionStatus: 'disconnected' | 'connected' | 'connecting' = 'connecting';

  private statusCbs: Array<(s: CallStatus) => void> = [];
  private endCbs: Array<() => void> = [];
  private peerAcceptCbs: Array<() => void> = [];
  private peerRejectCbs: Array<() => void> = [];
  private unansweredCbs: Array<() => void> = [];

  type: 'official' = 'official';
  direction: CallDirection = 'OUTGOING';
  peer: { phone: string; displayName: string | null; profilePicture: string | null; muted: boolean };

  constructor(private webphone: GrowhatsWebphone, public id: string, public phone: string, public device_token: string, displayName: string | null = null) {
    this.peer = { phone, displayName, profilePicture: null, muted: false };
  }

  get status() { return this._status; }
  get muted() { return this._muted; }
  get connection_status() { return this._connectionStatus; }

  _setStatus(s: CallStatus) {
    this._status = s;
    this.statusCbs.forEach(cb => cb(s));
    if (s === 'ACTIVE') this.peerAcceptCbs.forEach(cb => cb());
    if (s === 'REJECTED') this.peerRejectCbs.forEach(cb => cb());
    if (s === 'ENDED' || s === 'FAILED' || s === 'DISCONNECTED' || s === 'NOT_ANSWERED') {
      this.endCbs.forEach(cb => cb());
      if (this.durationTimer) clearInterval(this.durationTimer);
    }
  }

  async mute(): Promise<{ err: string | null }> { return this.webphone.muteAudio(); }
  async unmute(): Promise<{ err: string | null }> { return this.webphone.unmuteAudio(); }
  async end(): Promise<{ err: string | null }> { return this.webphone.endCall(this.id); }

  onPeerAccept(cb: () => void) { this.peerAcceptCbs.push(cb); }
  onPeerReject(cb: () => void) { this.peerRejectCbs.push(cb); }
  onUnanswered(cb: () => void) { this.unansweredCbs.push(cb); }
  onEnd(cb: () => void) { this.endCbs.push(cb); }
  onStatus(cb: (status: CallStatus) => void) { this.statusCbs.push(cb); }
}
